import React, {useMemo} from 'react';
import {classes, style, vars} from './doppe-layout10-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps} from '../../../doppe-sdk';
import {RatioMaintainer} from '@wix/devzai-utils-react';
import {arrayFirst, iterableAny} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {DoppeProfileNameAutoSource} from '../../../client-server-common';
import {
    BymoTemplateSpeedDialActionsListAppearance,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {DoppeLayoutRoot} from '../dopp-layouts-components/doppe-layout-root/doppe-layout-root';
import {DoppLayoutBackground} from '../dopp-layouts-components/dopp-layout-background/dopp-layout-background';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {
    DoppLayoutContentHeader
} from '../dopp-layouts-components/dopp-layout-content-header/dopp-layout-content-header';
import {DoppePageHeroPhoto} from '../../doppe-page-hero-photo/doppe-page-hero-photo';
import {DoppLayoutContentTitle} from '../dopp-layouts-components/dopp-layout-content-title/dopp-layout-content-title';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {
    DoppePageProfileNameView,
    DoppePageProfileNameViewVerifiedCheckAppearance
} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {DoppeLayoutActionsList} from '../dopp-layouts-components/doppe-layout-actions-list/doppe-layout-actions-list';
import {
    DoppeLayoutSearchSection
} from '../dopp-layouts-components/doppe-layout-search-action/doppe-layout-search-section';
import {DoppeLayoutBioText} from '../dopp-layouts-components/doppe-layout-bio-text/doppe-layout-bio-text';
import {DoppeLayoutSocialLinks} from '../dopp-layouts-components/doppe-layout-social-links/doppe-layout-social-links';

export const DoppeLayout10ProfileViewTemplate = React.memo(function DoppeLayout5ProfileView(props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>) {
    const {
        profilePictureLinkInBio,
        bioText,
        pageTitle,
        actionsHeading,
        bioTextMaxLines,
        socialLinks,
        searchAppearance,
        actionButtonTemplate,
        actionsListAppearance,
        actions,
        callToActionText,
        heroPhotos,
        searchPlaceholder,
        showActionsInSearch,
        heroPhotosOpacity
    } = props;

    const searchSectionIsVisible = searchAppearance !== BymoTemplateSpeedDialSearchAppearance.Hidden;

    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);

    const hasActions = useMemo(() => {
        return iterableAny(actions, action => action.enabled);
    }, [actions]);

    return (
        <DoppeLayoutRoot
            className={style(classes.root, {})}
            style={{
                [vars.opacity]: `${heroPhotosOpacity}%`,
            }}
            hideHoppBanner={props.removeHoppBanner}
        >
            <DoppLayoutBackground
                className={style(classes.coverBackground, {})}
            >
                <DoppLayoutBackgroundMedia
                    className={style(classes.pageWithCoverBackgroundView, {})}
                />
            </DoppLayoutBackground>
            <div
                className={style(classes.profileView, FlexLayout.column.default)}
            >
                <div
                    className={style(classes.profileImageAndName, {}, FlexLayout.column.alignToCenter)}
                >
                    {
                        hasProfileImage ?
                            <DoppeHideableValueRenderer
                                hideableValue={profilePictureLinkInBio}
                                renderFunction={profilePicture => (
                                    <DoppePageProfilePicture
                                        className={style(classes.profileImage, {})}
                                        profilePicture={profilePicture}
                                        size={100}
                                    />
                                )}
                            />
                            :
                            null
                    }
                    <DoppePageProfileNameView
                        className={style(classes.profileName, {})}
                        defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                        verifiedCheckAppearance={DoppePageProfileNameViewVerifiedCheckAppearance.Superscript}
                    />
                </div>
                <DoppeLayoutBioText
                    className={style(classes.bioText, {})}
                    bioText={bioText}
                    bioTextMaxLines={bioTextMaxLines}
                    lineHeight={'18px'}
                />
                <DoppeLayoutSocialLinks
                    className={style(classes.socialLinks, {}) }
                    socialLinks={socialLinks}
                />
            </div>
            {
                heroPhotos !== null && heroPhotos.length > 0 ?
                    <RatioMaintainer
                        className={style(classes.profileImageContainer, {})}
                        widthHeightRatio={1}
                    >
                        <DoppePageHeroPhoto
                            className={style(classes.heroPhoto)}
                            mediaResource={heroPhotos === null ? null : (arrayFirst(heroPhotos) ?? null)}
                            layoutSpec={{
                                widthHeightRatio: 1
                            }}
                            widthSpec={{contentRelativeWidth: 1}}
                        />
                    </RatioMaintainer>
                    :
                    null
            }
            {
                searchSectionIsVisible || doppeHideableValueIsVisibleAndNotEqualValue(pageTitle, '') ?
                    <div
                        className={style(classes.searchContainer, {})}
                    >
                        <DoppLayoutContentTitle
                            text={pageTitle}
                        />
                        <DoppeLayoutSearchSection
                            actionButtonTemplate={actionButtonTemplate}
                            searchPlaceholder={searchPlaceholder}
                            showActionsInSearch={showActionsInSearch}
                            searchAppearance={searchAppearance}
                            callToActionText={callToActionText}
                        />
                    </div>
                    :
                    null
            }
            <DoppLayoutContent
                className={style(classes.content)}
            >
                <div
                    className={style(classes.pageContent, {})}
                >
                    {actionsListAppearance !== BymoTemplateSpeedDialActionsListAppearance.Hidden ? (
                        <>
                            <DoppLayoutContentHeader
                                text={actionsHeading}
                            />
                            <DoppeLayoutActionsList
                                hasActions={hasActions}
                                className={style(classes.actionsList, {})}
                                actionButtonTemplate={actionButtonTemplate}
                            />
                        </>
                    ) : null}
                </div>
            </DoppLayoutContent>
        </DoppeLayoutRoot>
    )
});

